import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errorMessage } from "../api/client";
import type { ListParams } from "../components";

export type Tenant = { id: string; name: string; slug: string; status: string; timezone: string; base_currency: string; created_at: string; member_count: number; site_count: number; owners: string[];
  subscription: { plan: string | null; status: string; trial_days_left: number | null; seats: number | null; modules: string[]; limits: Record<string, number>; read_only: boolean } };

async function unwrap<T>(p: Promise<{ data?: unknown; error?: unknown }>): Promise<T> {
  const { data, error } = await p;
  if (error) throw new Error(errorMessage(error));
  return data as T;
}

export function useTenants(params: ListParams) {
  return useQuery({ queryKey: ["console-tenants", params], queryFn: () => unwrap<{ count: number; results: Tenant[] }>(api.GET("/api/v1/console/organizations/", { params: { query: params as never } })) });
}
export function useTenant(id: string | undefined) {
  return useQuery({ queryKey: ["console-tenant", id], enabled: !!id, queryFn: () => unwrap<Tenant>(api.GET("/api/v1/console/organizations/{id}/", { params: { path: { id: id! } } })) });
}
export function useTenantNotes(id: string | undefined) {
  return useQuery({ queryKey: ["console-notes", id], enabled: !!id, queryFn: () => unwrap<{ id: string; text: string; author: string; created_at: string }[]>(api.GET("/api/v1/console/organizations/{id}/notes/", { params: { path: { id: id! } } })) });
}
export function useTenantActions(id: string | undefined) {
  return useQuery({ queryKey: ["console-actions", id], enabled: !!id, queryFn: () => unwrap<{ id: string; action: string; actor: string; reason: string; data: Record<string, unknown>; created_at: string }[]>(api.GET("/api/v1/console/organizations/{id}/actions/", { params: { path: { id: id! } } })) });
}
export function useTenantFlags(id: string | undefined) {
  return useQuery({ queryKey: ["console-flags", id], enabled: !!id, queryFn: () => unwrap<{ flags: Record<string, boolean>; declared: Record<string, string> }>(api.GET("/api/v1/console/organizations/{id}/flags/", { params: { path: { id: id! } } })) });
}
export function useTenantOp(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ op, body }: { op: "status" | "extend-trial" | "overrides" | "flags" | "notes"; body: Record<string, unknown> }) => {
      const path = `/api/v1/console/organizations/{id}/${op}/` as "/api/v1/console/organizations/{id}/notes/";
      return unwrap(api.POST(path, { params: { path: { id } }, body: body as never }));
    },
    onSuccess: () => ["console-tenant", "console-tenants", "console-notes", "console-actions", "console-flags"].forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
}
export function useImpersonate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { organization: string; email: string; reason: string; minutes: number; allow_writes: boolean }) => unwrap(api.POST("/api/v1/console/impersonate/", { body: body as never })),
    onSuccess: () => { qc.clear(); window.location.assign("/app"); },
  });
}
export function useMetrics(days: number) {
  return useQuery({ queryKey: ["console-metrics", days], queryFn: () => unwrap<Record<string, unknown>>(api.GET("/api/v1/console/metrics/", { params: { query: { days } } as never })) });
}
